import { Button, Center, Group, Pagination, Stack, Table, ActionIcon, Text } from '@mantine/core'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryAdvertisers, useDeleteAdvertiser } from 'hooks/advertisers'
import { useAppTranslation } from 'hooks/useTranslation'
import { IconEdit, IconTrash } from '@tabler/icons-react'
import { YesNoDialog } from 'components/YesNoDialog'
import { useDisclosure } from '@mantine/hooks'
import { useState } from 'react'

const perPage = 10

export const Advertisers = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const page = parseInt(searchParams.get('page') || '1', 10)
  const { t } = useAppTranslation()
  const [opened, { open, close }] = useDisclosure(false)
  const [deleteId, setDeleteId] = useState('')

  const deleteAdvertiserMutation = useDeleteAdvertiser(() => {
    close()
  })

  const { isLoading, isError, error, advertisers, count } = useQueryAdvertisers({
    page,
    perPage,
  })

  if (isError) {
    return (
      <div>
        {t('common.error')}
        {error?.message}
      </div>
    )
  }
  if (isLoading) {
    return <div>{t('common.loading')}</div>
  }

  const changePage = (page: number) => {
    setSearchParams({ page: page.toString() })
  }

  const goToCreateAdvertiser = () => {
    navigate(`${window.location.pathname}/create`)
  }

  const editAdvertiser = (id: string) => {
    navigate(`${window.location.pathname}/${id}`)
  }

  const deleteAdvertiserDialog = (id: string) => {
    setDeleteId(id)
    open()
  }

  const deleteAdvertiser = async () => {
    await deleteAdvertiserMutation.mutateAsync({ id: deleteId })
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Text size="xl" fw={600}>
          {t('advertisers.title')}
        </Text>
        <Button onClick={goToCreateAdvertiser}>{t('advertisers.create_advertiser')}</Button>
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('advertisers.name')}</Table.Th>
            <Table.Th>{t('advertisers.category')}</Table.Th>
            <Table.Th>{t('advertisers.contact_email')}</Table.Th>
            <Table.Th>{t('advertisers.contact_phone')}</Table.Th>
            <Table.Th style={{ width: 100 }}>{t('common.actions')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {advertisers.map((advertiser) => (
            <Table.Tr
              key={advertiser.id}
              style={{ cursor: 'pointer' }}
              onClick={() => editAdvertiser(advertiser.id)}
            >
              <Table.Td>{advertiser.name}</Table.Td>
              <Table.Td>{advertiser.category}</Table.Td>
              <Table.Td>{advertiser.contact_email}</Table.Td>
              <Table.Td>{advertiser.contact_phone || '-'}</Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    onClick={(e) => {
                      e.stopPropagation()
                      editAdvertiser(advertiser.id)
                    }}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteAdvertiserDialog(advertiser.id)
                    }}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {count > perPage && (
        <Center mt="xl">
          <Pagination
            mr="xl"
            value={page}
            total={Math.ceil(count / perPage)}
            onChange={changePage}
          />
        </Center>
      )}

      <YesNoDialog
        opened={opened}
        title={t('common.delete') + ' ' + t('advertisers.title').slice(0, -1)}
        question={t('advertisers.delete_confirmation')}
        onClose={close}
        onYes={deleteAdvertiser}
      />
    </Stack>
  )
}